import { filterWithKeys } from "../../../../core/helpers/map-util.js";
import Model from "../../../Model.js";
import Report from "../../../reports/reports/Report.js";

/**
 * Abstract model for a Forecast report. Subclasses are used to
 * discriminate the kind of forecast report we are dealing with.
 *
 * @abstract
 * @param {Report} report - Report specific to the current Forecast
 */
export default class AbstractForecastReport extends Model {
	constructor({ report = new Report() } = {}) {
		super(...arguments);

		this._report = report;
		this._keyFigureValues = new Map();
	}

	/**
	 * Visiting method.  See `reportVisitor` for all visiting methods.
	 */
	accept(visitor) {
		return visitor.visitForecastReportReport(this);
	}

	getReport() {
		return this._report;
	}

	getTitleString() {
		return this.getReport().getTitleString();
	}

	setKeyFigureValues(keyFigureValues = new Map()) {
		this._keyFigureValues = keyFigureValues;
	}

	getId() {
		return this.constructor.getType();
	}

	getTitle() {
		return this._subclassResponsibility();
	}

	getSummaryRowTitle() {
		return this._subclassResponsibility();
	}

	getColumns() {
		return this._report.getColumns();
	}

	getColumnMatching(...rest) {
		return this._report.getColumnMatching(...rest);
	}

	getColumn({ start }) {
		return this.getColumnMatching((column) =>
			column.getStart().equal(start),
		);
	}

	getVisibleColumns() {
		return this._report.getVisibleColumns();
	}

	getLeafVisibleColumns() {
		return this._report.getLeafVisibleColumns();
	}

	getVisibleRows() {
		return this._report.getVisibleRows();
	}

	getRowByStableId(stableRowId) {
		return this.getAllRows().find(
			(row) => row.getStableId() === stableRowId,
		);
	}

	getRows() {
		return this._report.getRows();
	}

	getAllRows() {
		return this._report.getAllRows();
	}

	getVisibleRowForAccountNumber(accountNumber) {
		return this.getAllRows().find(
			(row) => row.isForAccountNumber(accountNumber) && row.isVisible(),
		);
	}

	getRowForAccountNumber(accountNumber) {
		return this.getAllRows().find((row) =>
			row.isForAccountNumber(accountNumber),
		);
	}

	hasRowForAccountNumber(accountNumber) {
		return !!this.getRowForAccountNumber(accountNumber);
	}

	getOperationType({ row: _ }) {
		return this._subclassResponsibility();
	}

	getDisplaySettings() {
		return this._report.getDisplaySettings();
	}

	setDisplaySettings(displaySettings) {
		this._report.setDisplaySettings(displaySettings);
	}

	getFormattedData({ row, column, showDash }) {
		return this._report.getFormattedData({ row, column, showDash });
	}

	getSummaryValueKeys() {
		return [];
	}

	isIncomeStatement() {
		return false;
	}

	isBalanceSheet() {
		return false;
	}

	isCashFlow() {
		return false;
	}

	hasSummaryRow({ groupRow }) {
		return this._report.hasSummaryRow({ groupRow });
	}

	isRowVisible(row) {
		return this._report.isRowVisible(row);
	}

	expandRowsUpToDefaultLevel() {
		this._report.expandRowsUpToDefaultLevel();
	}

	withCoALanguage(callback) {
		this._report.withCoALanguage(callback);
	}

	setupDefaultAccountVisibility() {
		this.getDisplaySettings().setAccountVisibility(
			this.constructor.getDefaultAccountVisibility(),
		);
	}

	getChartValues() {
		return filterWithKeys({
			map: this._keyFigureValues,
			keys: this._getChartKeyFigureIds(),
		});
	}

	getKeyFigureValuesForSummaryRow() {
		const keyFigureId = this._getSummaryRowKeyFigureId();
		return this._keyFigureValues?.get(keyFigureId) || [];
	}

	getChartStrategy() {
		return this._subclassResponsibility();
	}

	getChartDataTestName() {
		return this._subclassResponsibility();
	}

	getFiscalYearCardRenderingStrategy() {
		return this._subclassResponsibility();
	}

	/**
	 * Should return an object compatible with the `setValues()`
	 * method of the strategy returned by
	 * `getFiscalYearCardRenderingStrategy()`.
	 */
	getValuesForCard() {
		return this._subclassResponsibility();
	}

	getReferenceColumns() {
		return this.getColumns().filter((column) =>
			column.isReferenceForecastColumn(),
		);
	}

	getCurrentPeriodColumn() {
		return this.getVisibleColumns().find((column) =>
			column.isCurrentPeriodForecastColumn(),
		);
	}

	getCurrentPeriod() {
		return this.getCurrentPeriodColumn()?.getPeriod();
	}

	getPredictedColumns() {
		return this.getColumns().filter((column) =>
			column.isPredictedForecastColumn(),
		);
	}

	getPredictedClosingMonthColumnIndex() {
		return this.getVisibleColumns().findIndex(
			(column) =>
				column.isPredictedForecastColumn() && column.isClosing(),
		);
	}

	getKeyFigureOperationType(keyFigure) {
		return keyFigure.getOperationType() || this.getOperationType();
	}

	makeComputationSeriesSpecifications({ builder }) {
		this._addComputationSeriesSpecificationsForRows({ builder });
		this._addComputationSeriesSpecificationsForKeyFigures({ builder });
		this._addComputationSeriesSpecificationsForCard({ builder });
		this._addComputationSeriesSpecificationsForChart({ builder });
		this._addComputationSeriesSpecificationsForSummaryRow({ builder });
		return builder.build();
	}

	setDataFromSeries(series) {
		this._setDataIntoRowsFromSeries(series);
		this._setKeyFigureValuesFromSeries(series);
		this._setDataForCardFromSeries(series);
		this._setDataForChartFromSeries(series);
		this._setDataForSummaryRowFromSeries(series);
	}

	rulesRendererFor(_forecastCellInfoWidget) {
		return this._subclassResponsibility();
	}

	getAvailableRuleModelClasses({ row: _row }) {
		return [];
	}

	_addComputationSeriesSpecificationsForRows({ builder }) {
		builder.withRowSeriesSpecification({ report: this });
	}

	_setDataIntoRowsFromSeries(series) {
		series.setDataIntoReport(this);
	}

	_addComputationSeriesSpecificationsForKeyFigures({ builder }) {
		this._getAllKeyFigureIds().forEach((keyFigureId) => {
			builder.withKeyFigureSeriesSpecification({
				keyFigureId,
				report: this,
			});
		});
	}

	_setKeyFigureValuesFromSeries(series) {
		this._getAllKeyFigureIds().forEach((keyFigureId) => {
			const keyFigureValues = series.getKeyFigureValues({
				keyFigureId,
				report: this,
			});

			this._keyFigureValues.set(keyFigureId, keyFigureValues);
		});
	}

	_addComputationSeriesSpecificationsForCard() {}

	_setDataForCardFromSeries(_series) {}

	_addComputationSeriesSpecificationsForChart() {}

	_setDataForChartFromSeries(_series) {}

	_addComputationSeriesSpecificationsForSummaryRow() {}

	_setDataForSummaryRowFromSeries(_series) {}

	_getColumnTitles() {
		return this.getColumns().map((column) => column.getTitle());
	}

	_getAllKeyFigureIds() {
		return [
			...new Set([
				this._getSummaryRowKeyFigureId(),
				...this._getCardKeyFigureIds(),
				...this._getChartKeyFigureIds(),
			]),
		];
	}

	_getSummaryRowKeyFigureId() {
		return this.constructor.getSummaryRowKeyFigureId();
	}

	_getChartKeyFigureIds() {
		return this.constructor.getChartKeyFigureIds();
	}

	_getCardKeyFigureIds() {
		return this.constructor.getCardKeyFigureIds();
	}

	_getOpeningAndClosingColumnsFor({ closingColumnIndex }) {
		const openingColumnIndex = this._getOpeningColumnIndexFor({
			closingColumnIndex,
		});

		const columns = this.getColumns();
		const openingColumn = columns[openingColumnIndex];
		const closingColumn = columns[closingColumnIndex];

		return { openingColumn, closingColumn };
	}

	_getOpeningColumnIndexFor({ closingColumnIndex }) {
		return Math.max(
			0,
			closingColumnIndex -
				this._getYearLengthFor({ closingColumnIndex }) +
				1,
		);
	}

	_getYearLengthFor({ closingColumnIndex }) {
		const closingColumn = this.getColumns()[closingColumnIndex];

		return closingColumn?.getStart()?.getIndex();
	}

	static getDefaultAccountVisibility() {
		return this._subclassResponsibility();
	}

	static getType() {
		return this._subclassResponsibility();
	}

	static getSummaryRowKeyFigureId() {
		return undefined;
	}

	static getChartKeyFigureIds() {
		return [];
	}

	static getCardKeyFigureIds() {
		return [];
	}
}
